import clsx from "clsx";
import { navigate } from "gatsby";
import React, { DetailedHTMLProps, FC, HTMLAttributes, useEffect, useState } from "react";
import { usePrevious, useThrottle, useWindowScroll } from "react-use";

import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { SideCtaInnerLabel } from "./side-cta-inner-label";
import { SideCtaInnerShare } from "./side-cta-inner-share/side-cta-inner-share";
import { SideCtaInnerShareMobile } from "./side-cta-inner-share/site-cta-inner-share-mobile";
import { SideCtaItem } from "./side-cta-item";

export interface SideCtaItemsProps
  extends Queries.SideCtasDataFragment,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  isMobile: boolean;
}

export const SideCtaItems: FC<SideCtaItemsProps> = ({
  isMobile,
  supportLabel,
  supportPageLink,
  infoLabel,
  infoPageLink,
  shareEnabled,
  className,
}) => {
  const infoPagePath = usePageFullPath(infoPageLink?.id);
  const supportPagePath = usePageFullPath(supportPageLink?.id);

  const threshold = 10;
  const { y } = useWindowScroll();
  const previousScroll = usePrevious(y);
  const [shouldShowMenu, setShouldShowMenu] = useState(true);
  useEffect(() => {
    const deltaY = y - (previousScroll ?? 0);
    if (deltaY > threshold) {
      setShouldShowMenu(false);
    } else if (deltaY < -threshold) {
      setShouldShowMenu(true);
    }
  }, [y, previousScroll]);

  const shouldShowMenuThrottled = useThrottle(shouldShowMenu, 200);

  return (
    <div
      className={clsx(
        "transition-all duration-500",
        isMobile && !shouldShowMenuThrottled && "translate-y-[50vh]",
        className
      )}
    >
      {supportLabel && supportPagePath && (
        <SideCtaItem
          variant="light"
          icon="wrench"
          isMobile={isMobile}
          onClick={() => navigate(supportPagePath)}
          className="cursor-pointer"
        >
          <SideCtaInnerLabel isMobile={isMobile} label={supportLabel} />
        </SideCtaItem>
      )}
      {infoLabel && infoPagePath && (
        <SideCtaItem
          isMobile={isMobile}
          variant="neutral"
          icon="pencil"
          onClick={() => navigate(infoPagePath)}
          className="cursor-pointer"
        >
          <SideCtaInnerLabel isMobile={isMobile} label={infoLabel} />
        </SideCtaItem>
      )}
      {shareEnabled && !isMobile && (
        <SideCtaItem isMobile={isMobile} variant="neutral" icon="share">
          <SideCtaInnerShare />
        </SideCtaItem>
      )}
      {shareEnabled && isMobile && <SideCtaInnerShareMobile />}
    </div>
  );
};
